import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import { RecoveryFlow, UiNodeInputAttributes, UpdateRecoveryFlowBody } from '@ory/client';
import { AxiosError } from 'axios';
import { getOrySdk } from '../../services/ory';
import { handleFlowError } from '../../services/oryErrors';
import { useAuth } from '../../context';

export const RecoveryPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();

  const [flow, setFlow] = useState<RecoveryFlow | undefined>();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [step, setStep] = useState<'email' | 'code'>('email');

  // Get flow params from URL
  const flowId = searchParams.get('flow');
  const returnTo = searchParams.get('return_to');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Initialize or fetch recovery flow
  useEffect(() => {
    if (flow) {
      setIsLoading(false);
      return;
    }

    const sdk = getOrySdk();

    // If ?flow=... was in the URL, fetch it
    if (flowId) {
      sdk
        .getRecoveryFlow({ id: flowId })
        .then(({ data }) => {
          setFlow(data);
          checkForCodeStep(data);
        })
        .catch(handleFlowError(navigate, 'recovery', setFlow, setError))
        .catch((err) => {
          setError(err.message || 'Failed to load recovery flow');
        })
        .finally(() => setIsLoading(false));
      return;
    }

    // Otherwise create a new flow
    sdk
      .createBrowserRecoveryFlow({
        returnTo: returnTo ? String(returnTo) : undefined,
      })
      .then(({ data }) => {
        setFlow(data);
        // Update URL with flow ID
        window.history.replaceState(null, '', `/recovery?flow=${data.id}`);
      })
      .catch(handleFlowError(navigate, 'recovery', setFlow, setError))
      .catch((err) => {
        setError(err.message || 'Failed to initialize recovery');
      })
      .finally(() => setIsLoading(false));
  }, [flowId, flow, navigate, returnTo]);

  // Check if flow is at code step
  const checkForCodeStep = (flowData: RecoveryFlow) => {
    const hasCodeInput = flowData.ui.nodes?.some(
      (n) => (n.attributes as UiNodeInputAttributes)?.name === 'code'
    );
    if (hasCodeInput) {
      setStep('code');
      setSuccess('Recovery code sent! Check your email.');
    }
  };

  // Get CSRF token from flow
  const getCsrfToken = useCallback((): string => {
    let csrf = '';
    flow?.ui.nodes?.forEach((node) => {
      if (node.type === 'input') {
        const attrs = node.attributes as UiNodeInputAttributes;
        if (attrs.name === 'csrf_token' && attrs.value) {
          csrf = String(attrs.value);
        }
      }
    });
    return csrf;
  }, [flow]);

  // Handle email submission
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!flow) return;

    setIsSubmitting(true);
    setError(null);

    const sdk = getOrySdk();

    const body: UpdateRecoveryFlowBody = {
      method: 'code',
      email: email,
      csrf_token: getCsrfToken(),
    };

    try {
      const response = await sdk.updateRecoveryFlow({
        flow: flow.id,
        updateRecoveryFlowBody: body,
      });

      setFlow(response.data);
      checkForCodeStep(response.data);
    } catch (err) {
      const axiosErr = err as AxiosError<any>;

      try {
        await handleFlowError(navigate, 'recovery', setFlow, setError)(axiosErr);
      } catch (unhandledErr) {
        if (axiosErr.response?.status === 400) {
          setFlow(axiosErr.response.data);
          checkForCodeStep(axiosErr.response.data);
          if (!axiosErr.response.data.ui.nodes?.some(
            (n: any) => n.attributes?.name === 'code'
          )) {
            extractErrorMessages(axiosErr.response.data);
          }
        } else {
          setError(axiosErr.message || 'Failed to send recovery code.');
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle code submission
  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!flow) return;

    setIsSubmitting(true);
    setError(null);

    const sdk = getOrySdk();

    const body: UpdateRecoveryFlowBody = {
      method: 'code',
      code: code,
      csrf_token: getCsrfToken(),
    };

    try {
      await sdk.updateRecoveryFlow({
        flow: flow.id,
        updateRecoveryFlowBody: body,
      });

      // Recovery successful - redirect to settings to set new password
      navigate('/settings/security', {
        state: { message: 'You can now set a new password.' },
      });
    } catch (err) {
      const axiosErr = err as AxiosError<any>;

      try {
        await handleFlowError(navigate, 'recovery', setFlow, setError)(axiosErr);
      } catch (unhandledErr) {
        if (axiosErr.response?.status === 400) {
          setFlow(axiosErr.response.data);
          extractErrorMessages(axiosErr.response.data);
        } else if (axiosErr.response?.status === 410) {
          setError('Recovery link expired. Please start over.');
          setFlow(undefined);
          setStep('email');
        } else {
          setError(axiosErr.message || 'Invalid code. Please try again.');
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Extract error messages from flow UI
  const extractErrorMessages = (flowData: RecoveryFlow) => {
    const messages: string[] = [];

    flowData.ui.messages?.forEach((m) => {
      if (m.type === 'error') {
        messages.push(m.text);
      }
    });

    flowData.ui.nodes?.forEach((node) => {
      node.messages?.forEach((m) => {
        if (m.type === 'error') {
          messages.push(m.text);
        }
      });
    });

    if (messages.length > 0) {
      setError(messages.join(' '));
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="xs">
        <Paper sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" fontWeight={700} color="primary">
              FamilyPal
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {step === 'email' ? 'Reset your password' : 'Enter recovery code'}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {step === 'email' ? (
            <form onSubmit={handleEmailSubmit}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter your email address and we'll send you a recovery code.
              </Typography>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                autoComplete="email"
                autoFocus
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isSubmitting || !email}
                sx={{ mt: 3, mb: 2 }}
              >
                {isSubmitting ? <CircularProgress size={24} /> : 'Send Recovery Code'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleCodeSubmit}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the code we sent to {email}
              </Typography>
              <TextField
                fullWidth
                label="Recovery Code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                margin="normal"
                required
                autoFocus
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isSubmitting || !code}
                sx={{ mt: 3, mb: 2 }}
              >
                {isSubmitting ? <CircularProgress size={24} /> : 'Verify Code'}
              </Button>

              <Button
                fullWidth
                variant="text"
                onClick={() => {
                  setStep('email');
                  setSuccess(null);
                  setFlow(undefined);
                }}
              >
                Use a different email
              </Button>
            </form>
          )}

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link component={RouterLink} to="/login" variant="body2">
              Back to sign in
            </Link>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default RecoveryPage;
